import React from 'react';
import './styes/FooterPages.css';

const Privacy: React.FC = () => {
  return (
    <div className="footer-page">
      <h1>Privacy Policy</h1>
      
      <section>
        <h2>About this policy</h2>
        <p>
          This Privacy Policy explains how BSC INNOVATIONS PRIVATE LIMITED ("Company", "we", "us", "our"), operating the BuildZap platform, collects, uses, shares and protects your information when you use our website, mobile applications and related services (together, the "Platform").
        </p>
        <p>
          By accessing or using the Platform, you agree to the practices described in this Privacy Policy and our Terms of Use.
        </p>
      </section>

      <section>

        <p>
          The Grievance Officer is the contact point for privacy and user complaints and will acknowledge and resolve complaints within timelines prescribed by applicable laws.
        </p>
      </section>

      <section>
        <h2>What data we collect</h2>
        <p>Depending on how you use BuildZap, we may collect the following information.</p>
        <ul>
          <li><strong>Identity data:</strong> name, mobile number, email address, business name, GST details, KYC information (for B2B accounts, trade credit etc.).</li>
          <li><strong>Contact data:</strong> billing and shipping addresses, pincodes, alternate contact numbers.</li>
          <li><strong>Transaction data:</strong> order details, quotes, payment method, payment status, invoice details, refund and dispute information.</li>
          <li><strong>Technical data:</strong> IP address, device identifiers, browser type, operating system, app version, language, access times.</li>
          <li><strong>Usage data:</strong> pages viewed, product searches, clicks, time spent, referring pages, and interaction with app or site features and notifications.</li>
          <li><strong>Location data:</strong> approximate location from IP and precise location if you grant GPS permission in the app (for delivery and availability checks).</li>
          <li><strong>Communications data:</strong> messages, emails, call logs or chats with customer support, sales and operations teams.</li>
        </ul>
        <p>
          If you choose not to provide certain information, some services (such as placing orders or receiving deliveries) may not be available.
        </p>
      </section>

      <section>
        <h2>How we collect data</h2>
        <p>We collect information in the following ways.</p>
        <ul>
          <li><strong>Directly from you:</strong> when you create an account, place or manage orders, request quotes, upload KYC documents, contact support, or participate in surveys, referrals or promotions.</li>
          <li><strong>Automatically:</strong> through cookies, SDKs, pixels and similar technologies when you browse the website or use the mobile app.</li>
          <li><strong>From third parties:</strong> payment gateways, banks, logistics providers, analytics and advertising partners, KYC and credit information providers, or single sign-on providers (such as Google or WhatsApp, where enabled).</li>
        </ul>
      </section>

      <section>
        <h2>How we use your data</h2>
        <p>We use your data for purposes permitted under Indian data protection and e‑commerce laws, and rely on consent or other lawful grounds where required.</p>
        <ul>
          <li><strong>To operate BuildZap:</strong> create and manage accounts, authenticate users, maintain cart and orders, and provide core Platform features.</li>
          <li><strong>To fulfill orders and services:</strong> process and confirm orders, arrange dispatch and delivery, generate invoices, manage returns or cancellations, and provide customer support.</li>
          <li><strong>To personalize experience:</strong> show relevant products, categories, construction material recommendations and offers based on your activity and preferences.</li>
          <li><strong>To communicate with you:</strong> send order updates, delivery alerts, account notices, security alerts and responses to your queries via SMS, email, WhatsApp or calls.</li>
          <li><strong>For marketing (where permitted):</strong> send offers, newsletters, and promotions, and show targeted or remarketing ads on third‑party platforms; you can opt out of marketing communications at any time using the provided unsubscribe or preference options.</li>
          <li><strong>For analytics, security and compliance:</strong> monitor usage, improve performance, prevent fraud and abuse, enforce our Terms, and comply with tax, accounting and legal obligations.</li>
        </ul>
      </section>

      <section>
        <h2>Legal bases and consent (India)</h2>
        <p>For users in India, processing of personal data is done on one or more of the following grounds.</p>
        <ul>
          <li><strong>Consent:</strong> for certain activities (such as marketing communications and optional data sharing) after providing a clear notice; consent can be withdrawn at any time for future processing.</li>
          <li><strong>Performance of a contract:</strong> to register you, process orders, deliver products and provide customer support.</li>
          <li><strong>Legal obligations:</strong> to maintain records and respond to lawful requests under tax, accounting, consumer protection, and data protection laws.</li>
          <li><strong>Legitimate interests:</strong> to improve our Platform, prevent fraud, maintain IT and network security, and conduct business analytics, balanced with your rights and expectations.</li>
        </ul>
        <p>
          You can manage consent or request changes by contacting the Grievance / Data Protection Officer.
        </p>
      </section>

      <section>
        <h2>Cookies and tracking</h2>
        <p>BuildZap uses cookies and similar technologies on the website and app.</p>
        <ul>
          <li><strong>Essential cookies:</strong> needed for login, security, cart and checkout.</li>
          <li><strong>Analytics cookies:</strong> help us understand traffic and usage patterns (for example, via analytics services) to improve performance and features.</li>
          <li><strong>Advertising cookies/identifiers:</strong> used by us and our partners to show relevant ads and measure campaign effectiveness.</li>
        </ul>
        <p>
          You can manage cookies via your browser or device settings; however, disabling some cookies may impact certain features.
        </p>
      </section>

      <section>
        <h2>How we share your data</h2>
        <p>We do not sell your personal data.</p>
        <p>We may share information with the following categories of recipients for the purposes described above.</p>
        <ul>
          <li><strong>Service providers:</strong> delivery and logistics partners, warehousing partners, payment gateways and banks, cloud hosting providers, SMS/email/WhatsApp gateways, analytics, marketing and customer support tools that process data on our instructions.</li>
          <li><strong>Sellers/suppliers:</strong> to the extent needed to process your orders, handle dispatch and product‑related issues.</li>
          <li><strong>Business and marketing partners:</strong> for joint campaigns or programs, subject to appropriate safeguards and your choices.</li>
          <li><strong>Government, regulators and law enforcement:</strong> when required to respond to lawful requests, court orders, or to enforce our rights and protect users or the public.</li>
          <li><strong>Transactional events:</strong> if BSC INNOVATIONS PRIVATE LIMITED undergoes a merger, acquisition or asset sale, your data may be part of the transferred assets with similar protections.</li>
        </ul>
        <p>
          When sharing data, we require third parties to implement appropriate security and use data only for permitted purposes.
        </p>
      </section>

      <section>
        <h2>International transfers</h2>
        <p>
          Your information may be processed and stored on servers or by service providers located in or outside India.
          Where data is transferred outside India, we will ensure that such transfers comply with applicable law and that a comparable level of protection is provided through contractual or other safeguards.
        </p>
      </section>

      <section>
        <h2>Data retention</h2>
        <p>We retain personal data only for as long as necessary for the purposes described in this Privacy Policy or as required by law.</p>
        <ul>
          <li><strong>Account/profile data:</strong> kept while your account remains active and for a reasonable period afterward for dispute resolution, fraud prevention and legal compliance.</li>
          <li><strong>Transaction and invoice data:</strong> retained for periods mandated by tax, accounting and regulatory requirements.</li>
          <li><strong>Marketing data:</strong> retained until you opt out or request deletion, subject to technical and legal limits.</li>
        </ul>
        <p>
          When data is no longer needed, it is deleted, anonymized or securely archived in line with our internal policies.
        </p>
      </section>

      <section>
        <h2>Your rights and choices</h2>
        <p>Subject to Indian law, you may have the following rights regarding your personal data.</p>
        <ul>
          <li><strong>Access:</strong> request confirmation and a summary of personal data we process about you.</li>
          <li><strong>Correction:</strong> request correction or updating of inaccurate or incomplete data.</li>
          <li><strong>Deletion:</strong> request deletion of personal data no longer necessary for stated purposes, subject to legal retention requirements.</li>
          <li><strong>Consent withdrawal:</strong> withdraw consent for specific processing (such as marketing) for future use.</li>
          <li><strong>Grievance and appeal:</strong> raise a complaint with our Grievance / Data Protection Officer; further appeals may be available before designated authorities under applicable law once notified.</li>
        </ul>
        <p>
          You can exercise these rights by emailing hi@buildzap.in or contacting the Grievance / Data Protection Officer using the details above.
        </p>
      </section>

      <section>
        <h2>Data security</h2>
        <p>
          We use reasonable technical and organizational measures to protect your data against unauthorized access, alteration, disclosure or destruction.
        </p>
        <p>
          These may include access controls, encryption in transit and at rest where appropriate, secure development practices, periodic security reviews, and staff training.
          However, no method of transmission or storage is fully secure; users should keep login details confidential and use strong passwords.
        </p>
      </section>

      <section>
        <h2>Children's privacy</h2>
        <p>
          The Platform is intended for use by persons who can form a legally binding contract under Indian law, typically those aged 18 years or above.
          If you believe that a child has provided us with personal data without appropriate consent, please contact the Grievance / Data Protection Officer so that appropriate steps can be taken.
        </p>
      </section>

      <section>
        <h2>Third‑party links</h2>
        <p>
          Our Platform may contain links to third‑party websites, apps or services not operated by BSC INNOVATIONS PRIVATE LIMITED.
          Their privacy practices are governed by their own policies, and you should review those policies before sharing any personal data with them.
        </p>
      </section>

      <section>
        <h2>Changes to this Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technologies or legal requirements.
          When we make material changes, we will revise the "Last updated" date and may provide additional notice through the website, app or email where appropriate.
        </p>
      </section>

      <p className="last-updated">
        <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
      </p>
    </div>
  );
};

export default Privacy;
